import { useState, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { PLAN_CONFIG } from "@/lib/plans";

const stripePromise = loadStripe(
  "pk_test_51T3NsoAnj7ptF6WYNLqwgyWRW1e3C7LskgjbOWNHmsAJwSc6k9defAzUEixDytgEK0F0kZ8P1Gj4sm1rQT7UzFas00IEqvGh8x"
);

interface EmbeddedCheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceId: string;
  familyId?: string;
  couponCode?: string;
  planLabel?: string;
  onSuccess?: () => void;
}

export function EmbeddedCheckoutDialog({
  open,
  onOpenChange,
  priceId,
  familyId,
  couponCode,
  planLabel = "abonnement",
  onSuccess,
}: EmbeddedCheckoutDialogProps) {
  const [completed, setCompleted] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Resolve plan tier from priceId
  const planFromPrice = Object.entries(PLAN_CONFIG).find(
    ([, cfg]) => cfg.price_id === priceId
  )?.[0] as string | undefined;
  const fetchClientSecret = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: {
        priceId,
        familyId,
        couponCode,
        embedded: true,
      },
    });
    if (error) throw error;
    return data.clientSecret as string;
  }, [priceId, familyId, couponCode]);

  async function handleClose() {
    const wasCompleted = completed;
    setCompleted(false);
    setSyncing(false);
    onOpenChange(false);
    if (wasCompleted && onSuccess) onSuccess();
  }

  async function handleCheckoutComplete() {
    setCompleted(true);
    setSyncing(true);
    try {
      // Wait a few seconds for Stripe webhook to process
      await new Promise((r) => setTimeout(r, 3000));
      // Optimistically write the plan to the local DB so UI updates instantly
      if (user && planFromPrice) {
        const plan = (planFromPrice === "together" ? "plus" : planFromPrice) as "free" | "plus" | "together";
        await supabase.from("subscriptions").upsert(
          [{ user_id: user.id, plan }],
          { onConflict: "user_id" }
        );
      }
      // Invalidate subscription query so UI picks up the optimistic write
      await queryClient.invalidateQueries({ queryKey: ["subscription"] });
      await queryClient.invalidateQueries({ queryKey: ["family-entitlement"] });
      // Prevent background sync from overwriting optimistic data for 45s
      // by setting its data fresh (resets staleTime countdown)
      queryClient.setQueryData(["subscription-sync", user?.id], null);
      // Delay the full Stripe sync by 30s to give Stripe time to process
      setTimeout(() => {
        supabase.functions.invoke("check-subscription").then(() => {
          queryClient.invalidateQueries({ queryKey: ["subscription"] });
          queryClient.invalidateQueries({ queryKey: ["family-entitlement"] });
        }).catch(() => {});
      }, 30_000);
    } catch (e) {
      console.warn("Post-checkout sync failed, will retry on next interval", e);
    } finally {
      setSyncing(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
        {completed ? (
          <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
            <div className="rounded-full bg-primary/10 p-4">
              <CheckCircle2 className="h-12 w-12 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Betaling fullført! 🎉</h2>
            <p className="text-muted-foreground">
              {planLabel} er nå aktivert. Velkommen!
            </p>
            <Button className="w-full max-w-xs mt-2" onClick={handleClose} disabled={syncing}>
              {syncing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Aktiverer…</> : "Fortsett"}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-2">
              <DialogTitle>Start {planLabel}</DialogTitle>
              <DialogDescription>
                Fyll inn betalingsinfo for å komme i gang.
              </DialogDescription>
            </DialogHeader>
            <div className="px-2 pb-4 min-h-[400px]">
              {open && (
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={{
                    fetchClientSecret,
                    onComplete: handleCheckoutComplete,
                  }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
